import {
  Container,
  Paper,
  Text,
  Button,
  Select,
  TextInput,
  SimpleGrid,
  Group,
  ScrollArea,
  SelectItemProps,
  Modal,
  Title,
  ActionIcon,
  Radio,
  Stack,
  Tooltip,
  Divider,
  Checkbox,
  Textarea,
  Box,
} from "@mantine/core";
import {
  IconCirclePlus,
  IconInfoCircle,
  IconNotes,
  IconSettings,
  IconTrash,
  IconPencil,
} from "@tabler/icons-react";
import {
  addDoc,
  collection,
  collectionGroup,
  doc,
  DocumentData,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  deleteDoc,
  where,
  Timestamp,
} from "firebase/firestore";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { auth, db } from "../../firebase/FirebaseCredential";
import { useUserContext } from "../../contextapi/UserContext";
import { logError } from "../ErrorLogService/ErrorLoggingService";
import { ENQUIRY_STATUSES, getNextActionForStatus, getNextStatusForStatus } from "../../utils/EnquiryStatusFlow";
import { useDisclosure } from "@mantine/hooks";
import BenchLoader from "../UIComponent/BenchLoader/BenchLoader";
import PermissionButton from "../PermissionComponent/PermissionButton";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import { showToast } from "../Utils/toastHelper";
import { ROUTES } from "../../routes/routesPaths";
import BenchBreadcrumbs from "../UIComponent/Components/BenchBreadcrumbs";
import BenchCard from "../UIComponent/Components/BenchCard";
import BenchHeading from "../pages/UI/BenchHeading";
import { DateInput } from "@mantine/dates";
import { DefaultSystemPermission } from "../PermissionComponent/DefaultSystemPermission";
import { buildRoute } from "../../routes/routeBuilder";

interface CustomSelectItemProps extends SelectItemProps {
  label: string;
  level: number;
}

interface Enquiry {
  first_name: string;
  last_name: string;
  mobile_number: string;
  email: string;
  source_id: string;
  campaign_id: string;
  status: string;
  service: string;
  service_Id: string;
  product: string;
  branch_name?: string;
  description: string;
  next_action: string;
  expected_closingdate: Date | null;
  next_attempt: Date;
  lead_owner: string;
  communicationPreference: any[];
  enquiry_id?: string;
}

const CustomSelectItem = React.forwardRef<
  HTMLDivElement,
  CustomSelectItemProps
>(({ label, level, value, ...others }, ref) => (
  <div ref={ref} {...others}>
    <Group noWrap>
      {value === "add_new_source" || value === "add_new_campaign" ? (
        <IconCirclePlus stroke={2} color="green" />
      ) : null}

      <Text
        style={{
          paddingLeft: `${10 + level * 40}px`,
        }}
      >
        {label}
      </Text>
    </Group>
  </div>
));

export default function EnquiryEditPage() {
  const { branchId, enquiryId } = useParams<{
    branchId: string;
    enquiryId: string;
  }>();

  const companyId = useAppSelector(
    (state: RootState) => state.session.company_id,
  );

  const { branchesList, loading: branchLoading } = useAppSelector(
    (state) => state.branchList,
  );

  const [enquiryData, setEnquiryData] = useState<Enquiry>({
    first_name: "",
    last_name: "",
    mobile_number: "",
    email: "",
    source_id: "",
    campaign_id: "",
    service: "",
    product: "",
    next_action: "",
    status: "",
    lead_owner: "",
    branch_name: "",
    service_Id: "",
    description: "",
    expected_closingdate: null,
    next_attempt: new Date(),
    communicationPreference: ["Whatsapp"],
    enquiry_id: "",
  });

  const [FilterId, setFilterId] = useState<string | null>(branchId || null);
  const { roleDetails } = useUserContext();
  const [leadOwnersList, setLeadOwnersList] = useState<any>([]);
  const [leadOwnersId, setLeadOwnersId] = useState<any>();
  const [services, setServices] = useState<any>([]);
  const [products, setProducts] = useState<any>([]);
  const [noteText, setNoteText] = useState("");
  const [statusSteps, setStatusSteps] = useState<
    { label: string; value: string }[]
  >([]);

  const [sourceDataList, setSourceDataList] = useState<any>([]);
  const [campaignDataList, setCampaignDataList] = useState<any>([]);
  const [newCampaignName, setNewCampaignName] = useState<string>("");
  const [newSourceName, setNewSourceName] = useState<string>("");

  const [campaignModalOpen, setCampaignModalOpen] = useState(false);
  const [sourceModalOpen, setSourceModalOpen] = useState(false);

  const [
    openedPrefixModal,
    { open: PrefixModalOpen, close: PrefixModalClose },
  ] = useDisclosure(false);
  const [prefixIncrementMode, setPrefixIncrementMode] = useState("");
  const [prefixEnquiryCode, setPrefixEnquiryCode] = useState("");
  const [autoPrefixValue, setAutoPrefixValue] = useState("");
  const [existingCustomPrefixValue, setExistingCustomPrefixValue] =
    useState("");
  const [customPrefixIncrementNumber, setCustomPrefixIncrementNumber] =
    useState("");
  const [customPrefix, setCustomPrefix] = useState("");
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [notes, setNotes] = useState<any[]>([]);
  const [editingNote, setEditingNote] = useState<{
    id: string;
    text: string;
  } | null>(null);
  const [editNoteModalOpen, setEditNoteModalOpen] = useState(false);
  const [conversionModalOpen, setConversionModalOpen] = useState(false);
  const navigate = useNavigate();
  const canUpdate = DefaultSystemPermission("enquiry", "update");

  // Fetch Enquiry Data
  const [historyEnquiries, setHistoryEnquiries] = useState<any[]>([]);

  useEffect(() => {
    const fetchEnquiry = async () => {
      if (!companyId || !enquiryId) return;
      try {
        setIsPageLoading(true);
        const docRef = doc(
          db,
          "companyprofile",
          companyId,
          "enquiries",
          enquiryId,
        );
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setEnquiryData({
            first_name: data.first_name || "",
            last_name: data.last_name || "",
            mobile_number: data.mobile_number || "",
            email: data.email || "",
            source_id: data.source_id || "",
            campaign_id: data.campaign_id || "",
            service: data.service_id || data.service || "",
            product: data.product || "",
            next_action: data.next_action || "",
            status: data.status || "",
            lead_owner: data.lead_owner || "",
            branch_name: data.branch_name || "",
            service_Id: data.service_id || "",
            description: data.description || "",
            expected_closingdate:
              data.expected_closingdate instanceof Timestamp
                ? data.expected_closingdate.toDate()
                : data.expected_closingdate
                  ? new Date(data.expected_closingdate)
                  : null,
            next_attempt:
              data.next_attempt instanceof Timestamp
                ? data.next_attempt.toDate()
                : data.next_attempt
                  ? new Date(data.next_attempt)
                  : new Date(),
            communicationPreference: data.communicationPreference || [
              "Whatsapp",
            ],
            enquiry_id: data.enquiry_id || "",
          });
          setPrefixEnquiryCode(data.enquiry_id || "");
          setLeadOwnersId(data.lead_owner_id || "");
          setFilterId(data.branch_id || branchId);

          // Fetch history if mobile number exists
          if (data.mobile_number) {
            try {
              // Fetch all enquiries for the company and filter by mobile number in JS to avoid requiring a new composite index
              const q = query(
                collectionGroup(db, "enquiries"),
                where("company_id", "==", companyId),
                orderBy("created_at", "desc"),
              );
              const snapshot = await getDocs(q);
              const enquiryBranchId = data.branch_id || branchId;
              const docs = snapshot.docs
                .map((d) => ({ id: d.id, ...d.data() }))
                .filter(
                  (d: any) =>
                    d.mobile_number === data.mobile_number.trim() &&
                    (enquiryBranchId ? d.branch_id === enquiryBranchId : true) &&
                    !["converted", "enrolled", "lost", "unqualified"].includes(d.status?.toLowerCase()),
                );
              docs.sort(
                (a: any, b: any) =>
                  b.created_at?.toMillis?.() - a.created_at?.toMillis?.(),
              );
              setHistoryEnquiries(docs);
            } catch (err) {
              console.error("Error fetching history:", err);
            }
          }
        } else {
          toast.error("Enquiry not found");
          navigate(ROUTES.ENQUIRY); // Using ROUTES.ENQUIRY wait, let me use "/enquiry" directly to avoid issues if I typo'd it
        }
      } catch (error) {
        console.error("Error fetching enquiry:", error);
        logError(error, "fetchEnquiry EnquiryEditPage");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchEnquiry();
  }, [companyId, enquiryId, branchId, navigate]);

  const checkIsMember = async (): Promise<boolean> => {
    try {
      if (!companyId || !FilterId || !enquiryData.mobile_number) return false;
      const membersRef = collectionGroup(db, "members");

      let emailSnapshot: any = null;
      if (enquiryData.email.trim() !== "") {
        const emailQuery = query(
          membersRef,
          where("branch_id", "==", FilterId),
          where("company_id", "==", companyId),
          where("email", "==", enquiryData.email.trim().toLowerCase()),
        );
        emailSnapshot = await getDocs(emailQuery);
      }

      const mobileQuery = query(
        membersRef,
        where("branch_id", "==", FilterId),
        where("company_id", "==", companyId),
        where("mobile_number", "==", enquiryData.mobile_number),
      );
      const mobileSnapshot = await getDocs(mobileQuery);

      const emailHasDuplicates = emailSnapshot ? !emailSnapshot.empty : false;
      const mobileHasDuplicates = !mobileSnapshot.empty;

      return emailHasDuplicates || mobileHasDuplicates;
    } catch (error) {
      console.error("Error in checkIsMember:", error);
      return false;
    }
  };

  // Fetch Notes
  useEffect(() => {
    const fetchNotes = async () => {
      if (!companyId || !enquiryId || !FilterId) return;
      try {
        const notesRef = collection(
          db,
          "companyprofile",
          companyId,
          "enquiries",
          enquiryId,
          "enquirynotes",
        );
        // Applying security filters as required by Firestore rules
        const q = query(
          notesRef,
          where("company_id", "==", companyId),
          where("branch_id", "==", FilterId),
        );

        const querySnapshot = await getDocs(q);
        let fetchedNotes = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Sort notes by created_at desc (newest first)
        fetchedNotes.sort((a: any, b: any) => {
          const dateA = a.created_at?.toDate
            ? a.created_at.toDate()
            : new Date(a.created_at || 0);
          const dateB = b.created_at?.toDate
            ? b.created_at.toDate()
            : new Date(b.created_at || 0);
          return dateB.getTime() - dateA.getTime();
        });

        console.log(
          `Fetched ${fetchedNotes.length} notes for enquiry ${enquiryId}`,
        );
        setNotes(fetchedNotes);
      } catch (error) {
        console.error("Error fetching notes:", error);
        // If it's a permission error, it's likely due to missing indexes or rules
        if (error instanceof Error && error.message.includes("permission")) {
          console.warn(
            "Possible missing Firestore index or strict rule violation.",
          );
        }
      }
    };

    fetchNotes();
  }, [companyId, enquiryId, FilterId]);

  useEffect(() => {
    getEnquiryPrefixValue();
  }, [openedPrefixModal]);

  useEffect(() => {
    if (FilterId) {
      fetchSourchListData(FilterId);
      fetchCampaignListData(FilterId);
      fetchLeadOwner(FilterId);
      fetchBranch(FilterId);
    }
  }, [FilterId]);

  const getEnquiryPrefixValue = async () => {
    if (!companyId) return;
    try {
      const docRef = doc(
        db,
        "companyprofile",
        companyId,
        "companysettings",
        "customprefix",
      );
      const snap = await getDoc(docRef);
      if (!snap.exists()) return;
      const enquiry = snap.data().enquiry || {};
      const mode = enquiry.prefix_increment_mode;
      setPrefixIncrementMode(mode);
      setAutoPrefixValue(enquiry.prefix || "");
      setExistingCustomPrefixValue(enquiry.custom_prefix || "");
      setCustomPrefix(enquiry.custom_prefix || "");
      setCustomPrefixIncrementNumber(enquiry.custom_start_from || "");
    } catch (error) {
      console.error("Error in getEnquiryPrefixValue:", error);
    }
  };

  const fetchLeadOwner = async (branchId: string) => {
    try {
      const q = query(
        collection(db, "admin"),
        where("company_id", "==", companyId),
      );
      const querySnapshot = await getDocs(q);
      const results: any[] = [];
      querySnapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });

      const mappedLeadOwner = results.map((item) => ({
        label: item.first_name + " " + item.last_name,
        value: item.id,
      }));
      setLeadOwnersList(mappedLeadOwner);
    } catch (error) {
      console.error("Error fetching LeadOwner data:", error);
    }
  };

  const fetchSourchListData = async (branchId: string) => {
    try {
      const sourceCollection = await getDocs(
        query(
          collectionGroup(db, "sourceaccounts"),
          where("company_id", "==", companyId),
        ),
      );
      const sourceData = sourceCollection.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setSourceDataList(sourceData);
    } catch (error) {
      console.error("Error fetching sourcelist data:", error);
    }
  };

  const fetchCampaignListData = async (branchId: string) => {
    try {
      const campaignCollection = await getDocs(
        query(
          collectionGroup(db, "campaignaccounts"),
          where("company_id", "==", companyId),
        ),
      );
      const campaignData = campaignCollection.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setCampaignDataList(campaignData);
    } catch (error) {
      console.error("Error fetching campaignData data:", error);
    }
  };

  const fetchStatus = async () => {
    try {
      const statusDocRef = doc(db, "defaultconfig", "enquiry");
      const statusDocSnap = await getDoc(statusDocRef);

      if (statusDocSnap.exists()) {
        const formatted = ENQUIRY_STATUSES.map((item: string) => {
          let valueStr = item;
          const isDisabled =
            valueStr.includes("trial_booked") ||
            valueStr.includes("trial_completed") ||
            valueStr.includes("converted") ||
            valueStr.includes("lost") ||
            valueStr.includes("unqualified");

          return {
            label: valueStr
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c: any) => c.toUpperCase()),
            value: valueStr,
            disabled: isDisabled,
          };
        });
        const uniqueFormatted = Array.from(
          new Map(formatted.map((item: any) => [item.value, item])).values(),
        );
        setStatusSteps(uniqueFormatted as any);
      }
    } catch (err) {
      console.error("Error fetching status:", err);
    }
  };

  const handleNewCampaign = async () => {
    if (newCampaignName === "") {
      showToast.warn("requiredFieldMissing");
      return;
    }

    if (!FilterId || !companyId) {
      toast.error("Invalid Branch or Company ID.");
      return;
    }

    const data = {
      value: newCampaignName,
      label: newCampaignName,
      company_id: companyId,
    };
    try {
      const campaignCollectionRef = collection(
        db,
        "companyprofile",
        companyId,
        "campaignaccounts",
      );
      await addDoc(campaignCollectionRef, data);
      setCampaignModalOpen(false);
      setNewCampaignName("");
      fetchCampaignListData(FilterId);
      showToast.success("newCampaignAdded");
    } catch (error) {
      console.error("Error saving new campaign: ", error);
      toast.error("Failed to save new campaign.");
      logError(error, "handleNewCampaign EnquiryEditPage");
    }
  };

  const handleNewSource = async () => {
    if (newSourceName === "") {
      showToast.warn("requiredFieldMissing");
      return;
    }
    if (!FilterId || !companyId) {
      toast.error("Invalid Branch or Company ID.");
      return;
    }

    const data = {
      value: newSourceName,
      label: newSourceName,
      company_id: companyId,
      branch_id: FilterId,
    };

    try {
      setIsPageLoading(true);
      const sourceCollectionRef = collection(
        db,
        "companyprofile",
        companyId,
        "sourceaccounts",
      );
      let docRef = await addDoc(sourceCollectionRef, data);
      setSourceModalOpen(false);
      setNewSourceName("");
      fetchSourchListData(FilterId);
      setEnquiryData((prev) => ({
        ...prev,
        source_id: docRef.id || "",
      }));
      showToast.success("newSourceAdded");
    } catch (error) {
      console.error("Error saving new source: ", error);
      toast.error("Failed to save new source.");
      logError(error, "handleNewSource EnquiryEditPage");
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchBranch = async (branchId: string) => {
    try {
      const branchDocRef = doc(db, "branch", branchId);
      const branchDocSnap = await getDoc(branchDocRef);
      if (branchDocSnap.exists()) {
        const servicesData = await getDocs(
          query(
            collection(branchDocRef, "services"),
            where("company_id", "==", companyId),
          ),
        );
        setServices(
          servicesData.docs.map((doc) => ({ id: doc.id, ...doc.data() })),
        );
      }
    } catch (error) {
      console.error("Error fetching branch:", error);
    }
  };

  const handleUpdateEnquiry = async () => {
    if (!companyId || !enquiryId || !FilterId) {
      toast.error("Required data missing");
      return;
    }

    // Basic Validation
    if (enquiryData.first_name.trim() === "") {
      showToast.warn("enquiryNameRequired");
      return;
    }
    if (enquiryData.status.trim() === "") {
      toast.warn("Enquiry Status Required");
      return;
    }
    const indianPhoneRegex = /^[6-9]\d{9}$/;
    if (!indianPhoneRegex.test(enquiryData.mobile_number)) {
      showToast.warn("invalidMobileNumber");
      return;
    }

    try {
      setIsPageLoading(true);

      const campaignData = campaignDataList.find(
        (item: any) => item.id === enquiryData.campaign_id,
      );
      const sourceData = sourceDataList.find(
        (item: any) => item.id === enquiryData.source_id,
      );
      const serviceData = services.find(
        (item: any) => item.id === enquiryData.service,
      );
      const branchData = branchesList?.find(
        (item: any) => item.id === FilterId,
      );

      const updatedEnquiryData: any = {
        first_name: enquiryData.first_name.trim(),
        last_name: enquiryData.last_name.trim(),
        mobile_number: enquiryData.mobile_number,
        email: enquiryData.email.trim().toLowerCase(),
        source: sourceData?.label || enquiryData.source_id,
        source_id: enquiryData.source_id,
        campaign: campaignData?.label || enquiryData.campaign_id,
        campaign_id: enquiryData.campaign_id,
        service: serviceData?.name || enquiryData.service,
        service_id: enquiryData.service,
        product: enquiryData.product,
        status: enquiryData.status,
        next_action: enquiryData.next_action,
        description: enquiryData.description,
        expected_closingdate: enquiryData.expected_closingdate,
        next_attempt: enquiryData.next_attempt,
        communicationPreference: enquiryData.communicationPreference,
        branch_id: FilterId,
        branch_name: branchData?.name || enquiryData.branch_name,
        lead_owner_id: leadOwnersId,
        lead_owner: enquiryData.lead_owner,
        company_id: companyId,
        updated_at: serverTimestamp(),
        updated_by: auth.currentUser?.uid || "",
      };

      const enquiryRef = doc(
        db,
        "companyprofile",
        companyId,
        "enquiries",
        enquiryId,
      );
      await updateDoc(enquiryRef, updatedEnquiryData);

      // Add timeline entry
      const timelineRef = collection(enquiryRef, "enquirytimeline");
      await addDoc(timelineRef, {
        created_at: serverTimestamp(),
        action: "Enquiry Updated",
        description: `Enquiry updated for ${enquiryData.first_name} ${enquiryData.last_name}`,
        ...updatedEnquiryData,
        created_by_name: auth.currentUser?.displayName || "System",
      });

      // Handle Notes
      if (noteText.trim() !== "") {
        const notesRef = collection(enquiryRef, "enquirynotes");
        await addDoc(notesRef, {
          text: noteText.trim(),
          created_at: serverTimestamp(),
          created_by: auth.currentUser?.uid || "",
          created_by_name: auth.currentUser?.displayName || "",
          company_id: companyId,
          branch_id: FilterId,
        });
      }

      toast.success("Enquiry Updated Successfully");
      setTimeout(() => {
        navigate(
          buildRoute(ROUTES.ENQUIRY_VIEW, {
            branchId: FilterId,
            enquiryId: enquiryId,
          }) + "?tab=first",
        );
      }, 1000);
    } catch (error) {
      console.error("Error updating enquiry:", error);
      logError(error, "handleUpdateEnquiry EnquiryEditPage");
      toast.error("Failed to update enquiry.");
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleAddNoteUI = () => {
    if (!noteText.trim()) return;
    // In edit page we might want to save notes immediately or batch them.
    // Following add page logic, we'll keep them in noteText and save in handleUpdateEnquiry if single.
    // But if user clicks 'Add', we should probably save it immediately to sync with UI.
    saveNoteImmediately();
  };

  const saveNoteImmediately = async () => {
    if (!noteText.trim() || !companyId || !enquiryId || !FilterId) return;
    try {
      const notesRef = collection(
        db,
        "companyprofile",
        companyId,
        "enquiries",
        enquiryId,
        "enquirynotes",
      );
      const newNote = {
        text: noteText.trim(),
        created_at: serverTimestamp(),
        created_by: auth.currentUser?.uid || "",
        created_by_name: auth.currentUser?.displayName || "",
        company_id: companyId,
        branch_id: FilterId,
      };
      const docRef = await addDoc(notesRef, newNote);
      setNotes((prev) => [
        { id: docRef.id, ...newNote, created_at: new Date() },
        ...prev,
      ]);
      setNoteText("");
      toast.success("Note added");
    } catch (error) {
      console.error("Error adding note:", error);
    }
  };

  const handleDeleteNote = async (id: string) => {
    if (!canUpdate || !companyId || !enquiryId || !id) return;
    try {
      const noteRef = doc(
        db,
        "companyprofile",
        companyId,
        "enquiries",
        enquiryId,
        "enquirynotes",
        id,
      );
      await deleteDoc(noteRef);
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Note deleted permanently");
    } catch (error) {
      console.error("Error deleting note:", error);
      toast.error("Failed to delete note");
    }
  };

  const handleEditNote = (note: any) => {
    if (!canUpdate) return;
    setEditingNote({ id: note.id, text: note.text });
    setEditNoteModalOpen(true);
  };

  const handleUpdateNote = async () => {
    if (
      !canUpdate ||
      !editingNote ||
      !editingNote.text.trim() ||
      !companyId ||
      !enquiryId
    )
      return;
    try {
      const noteRef = doc(
        db,
        "companyprofile",
        companyId,
        "enquiries",
        enquiryId,
        "enquirynotes",
        editingNote.id,
      );
      await updateDoc(noteRef, {
        text: editingNote.text.trim(),
        updated_at: serverTimestamp(),
        company_id: companyId,
        branch_id: FilterId,
      });
      setNotes((prev) =>
        prev.map((n) =>
          n.id === editingNote.id ? { ...n, text: editingNote.text } : n,
        ),
      );
      setEditNoteModalOpen(false);
      setEditingNote(null);
      toast.success("Note updated");
    } catch (error) {
      console.error("Error updating note:", error);
      toast.error("Failed to update note");
    }
  };

  const breadcrumbLinks = [
    { label: "Enquiry", path: "/enquiry" },
    { label: "Edit Enquiry" },
  ];

  const handleBranchChange = (value: string | null) => {
    setFilterId(value);
  };

  const handleLeadOwner = (value: string | null) => {
    setLeadOwnersId(value);
    const selected = leadOwnersList.find((o: any) => o.value === value);
    setEnquiryData((prev) => ({ ...prev, lead_owner: selected?.label || "" }));
  };

  const handlePrefixCancelClick = () => {
    PrefixModalClose();
  };

  const handleEnquiryCodeSave = async () => {
    // Prefix saving logic (similar to Add Page)
    PrefixModalClose();
  };

  return (
    <div>
      <Container size="lg">
        {" "}
        <Box
          sx={(theme) => ({
            position: "sticky",
            top: 110, // Avoid overlapping the fixed navbar
            zIndex: 100,
            backgroundColor: "#FAFAFA",
            paddingTop: theme.spacing.sm,
            //paddingBottom: theme.spacing.xs,
            marginTop: `-${theme.spacing.sm}`,
          })}
        >
          <SimpleGrid cols={2}>
            <BenchBreadcrumbs links={breadcrumbLinks} />
            <Group position="right">
              <Button
                variant="outline"
                onClick={() => navigate(ROUTES.ENQUIRY)}
              >
                Cancel
              </Button>
              <PermissionButton
                resourceId="enquiry"
                actionType="update"
                label="Save"
                onClick={handleUpdateEnquiry}
              />
            </Group>
          </SimpleGrid>
          <Divider my="sm" />
        </Box>
        <BenchCard style={{ marginBottom: "40px" }}>
          <Group mb="md">
            <BenchHeading
              title="Edit Enquiry"
              subheading="Update the details for this business enquiry"
              order={4}
              fw={600}
              fz="h1"
            />
          </Group>
          {historyEnquiries.length > 1 && (
            <Select
              label="Select Enquiry (History)"
              description="This contact has multiple enquiries. Use this to switch to a different enquiry for this person."
              data={historyEnquiries.map((e) => ({
                value: e.id,
                label: `${e.enquiry_id || e.id} - ${e.status?.replace(/_/g, " ")} - ${e.product || e.service || "General"}`,
              }))}
              value={enquiryId}
              onChange={(val) => {
                if (val && val !== enquiryId) {
                  navigate(`/enquiry-edit/${FilterId || branchId}/${val}`);
                }
              }}
              mb="xl"
            />
          )}
          <SimpleGrid
            breakpoints={[
              { minWidth: "sm", cols: 1 },
              { minWidth: "md", cols: 2, spacing: "md" },
              { minWidth: "lg", cols: 2, spacing: "md" },
            ]}
          >
            <>
              <Select
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Select Branch</span>
                    <Tooltip label="Choose the branch where this enquiry is being recorded.">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                value={FilterId}
                onChange={(value) => handleBranchChange(value)}
                data={
                  branchesList
                    ?.filter((item: any) => item.id !== "all_branches")
                    ?.map((item: any) => ({
                      value: item.id,
                      label: item.name,
                    })) || []
                }
                placeholder="Select Branch"
                mb="lg"
                withAsterisk
                // Branch usually shouldn't change
              />
              <Select
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Lead owner</span>
                    <Tooltip label="Select the person responsible for handling this enquiry.">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                value={leadOwnersId}
                onChange={(value) => handleLeadOwner(value)}
                data={leadOwnersList}
                placeholder="Lead owner"
                mb="lg"
                withAsterisk
              />

              <TextInput
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>First Name</span>
                    <Tooltip label="Enter the first name of the person making the enquiry.">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                value={enquiryData.first_name}
                onChange={(e) => {
                  const regex = /^[A-Za-z\s]*$/;
                  if (regex.test(e.currentTarget.value)) {
                    setEnquiryData({
                      ...enquiryData,
                      first_name: e.currentTarget.value,
                    });
                  }
                }}
                mb="lg"
                required
              />
              <TextInput
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Last Name</span>
                    <Tooltip label="Enter the last name of the person making the enquiry.">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                value={enquiryData.last_name}
                onChange={(e) => {
                  const regex = /^[A-Za-z\s]*$/;
                  if (regex.test(e.currentTarget.value)) {
                    setEnquiryData({
                      ...enquiryData,
                      last_name: e.currentTarget.value,
                    });
                  }
                }}
                mb="lg"
              />

              <TextInput
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Enquiry Code</span>
                    <Tooltip label="Unique identifier for this enquiry">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                value={prefixEnquiryCode}
                disabled
                mb="lg"
                rightSection={
                  <ActionIcon onClick={PrefixModalOpen}>
                    <IconSettings stroke={2} />
                  </ActionIcon>
                }
              />

              <TextInput
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Mobile Number</span>
                    <Tooltip label="Provide a valid mobile number for contact.">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                value={enquiryData.mobile_number}
                onChange={(e) => {
                  const input = e.currentTarget.value;
                  if (input.length <= 10) {
                    setEnquiryData({ ...enquiryData, mobile_number: input });
                  }
                }}
                mb="lg"
                required
                type="tel"
              />
              <TextInput
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Email</span>
                    <Tooltip label="Enter the email address of the enquirer.">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                value={enquiryData.email}
                onChange={(e) =>
                  setEnquiryData({
                    ...enquiryData,
                    email: e.currentTarget.value.toLowerCase(),
                  })
                }
                mb="lg"
              />

              <Select
                nothingFound
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Source</span>
                    <Tooltip label="Specify how the enquiry was received (e.g., website, phone, walk-in).">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                placeholder="Source"
                value={enquiryData.source_id}
                onChange={(value) => {
                  if (value === "add_new_source") {
                    setSourceModalOpen(true);
                  } else {
                    setEnquiryData((prev) => ({
                      ...prev,
                      source_id: value || "",
                    }));
                  }
                }}
                dropdownComponent={ScrollArea}
                itemComponent={CustomSelectItem}
                data={[
                  ...(sourceDataList?.map((item: { id: any; label: any }) => ({
                    value: item.id,
                    label: item.label,
                  })) || []),
                  {
                    value: "add_new_source",
                    label: "Add New Source",
                  },
                ]}
                mb="lg"
              />

              <Select
                nothingFound
                label={
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>Campaign</span>
                    <Tooltip label="Select the marketing campaign associated with this enquiry.">
                      <IconInfoCircle
                        stroke={1}
                        size={16}
                        style={{ cursor: "pointer" }}
                      />
                    </Tooltip>
                  </div>
                }
                placeholder="Campaign"
                value={enquiryData.campaign_id}
                onChange={(value) => {
                  if (value === "add_new_campaign") {
                    setCampaignModalOpen(true);
                  } else {
                    setEnquiryData((prev) => ({
                      ...prev,
                      campaign_id: value || "",
                    }));
                  }
                }}
                dropdownComponent={ScrollArea}
                itemComponent={CustomSelectItem}
                data={[
                  ...(campaignDataList?.map(
                    (item: { id: any; label: any }) => ({
                      value: item.id,
                      label: item.label,
                    }),
                  ) || []),
                  {
                    value: "add_new_campaign",
                    label: "Add New Campaign",
                  },
                ]}
                mb="lg"
              />

              <Select
                label="Service Interested"
                placeholder="Select Service"
                data={
                  services?.map((s: any) => ({ value: s.id, label: s.name })) ||
                  []
                }
                value={enquiryData.service}
                onChange={(value) =>
                  setEnquiryData({ ...enquiryData, service: value || "" })
                }
                mb="lg"
              />

              <Select
                label="Product Interested"
                placeholder="Select Product"
                data={[
                  { value: "protein_powder", label: "Protein Powder" },
                  { value: "gym_gloves", label: "Gym Gloves" },
                  { value: "yoga_mat", label: "Yoga Mat" },
                ]}
                value={enquiryData.product}
                onChange={(value) =>
                  setEnquiryData({ ...enquiryData, product: value || "" })
                }
                mb="lg"
              />

              <DateInput
                label="Next Attempt"
                placeholder="Select date"
                value={enquiryData.next_attempt}
                onChange={(value) =>
                  value &&
                  setEnquiryData({ ...enquiryData, next_attempt: value })
                }
                mb="lg"
              />

              <DateInput
                label="Expected Closing Date"
                placeholder="Select date"
                value={enquiryData.expected_closingdate}
                onChange={(value) =>
                  setEnquiryData({
                    ...enquiryData,
                    expected_closingdate: value,
                  })
                }
                mb="lg"
              />

              <Select
                label="Status"
                placeholder="Select Status"
                data={statusSteps}
                value={enquiryData.status}
                onChange={async (value: any) => {
                  if (value === "enrolled") {
                    const isMember = await checkIsMember();
                    if (!isMember) {
                      setConversionModalOpen(true);
                      return;
                    }
                  }
                  const rawValue = value || "";
                  const normalizedValue = rawValue
                    .toLowerCase()
                    .replace(/ /g, "_");

                  const isTerminal = [
                    "converted",
                    "lost",
                    "unqualified",
                    "enrolled",
                  ].includes(normalizedValue);

                  let nextAction = "";
                  if (!isTerminal) {
                    const expectedNextAction = getNextStatusForStatus(normalizedValue);
                    if (expectedNextAction) {
                      const matchingStep = statusSteps.find(
                        (s) =>
                          s.value.replace(/ /g, "_") === expectedNextAction,
                      );
                      nextAction = matchingStep
                        ? matchingStep.value
                        : expectedNextAction;
                    }
                  }

                  setEnquiryData((prev) => ({
                    ...prev,
                    status: value || "",
                    next_action: nextAction,
                  }));
                }}
                mb="lg"
                searchable
              />

              <Select
                label="Next Action"
                placeholder="Auto-filled"
                data={statusSteps}
                value={enquiryData.next_action}
                onChange={(value) =>
                  setEnquiryData((prev) => ({
                    ...prev,
                    next_action: value || "",
                  }))
                }
                mb="lg"
              />

              <Textarea
                label="Description"
                placeholder="Enter description"
                value={enquiryData.description}
                onChange={(e) =>
                  setEnquiryData({
                    ...enquiryData,
                    description: e.currentTarget.value,
                  })
                }
              />
            </>
          </SimpleGrid>

          <Text size="sm" fw={500} mb={8} mt="xl">
            Communication Preference *
          </Text>
          <Group spacing="md">
            {["Phone", "Whatsapp", "Email"].map((label) => (
              <Paper
                key={label}
                withBorder
                radius="md"
                p="sm"
                w={190}
                display="flex"
                style={{
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Text>{label}</Text>
                <Checkbox
                  checked={enquiryData.communicationPreference.includes(label)}
                  onChange={(e) => {
                    const checked = e.currentTarget.checked;
                    setEnquiryData((prev) => ({
                      ...prev,
                      communicationPreference: checked
                        ? [...prev.communicationPreference, label]
                        : prev.communicationPreference.filter(
                            (item) => item !== label,
                          ),
                    }));
                  }}
                />
              </Paper>
            ))}
          </Group>

          <BenchCard mt="xl">
            <Group mb="md">
              <BenchHeading
                title="Notes"
                subheading="Internal remarks about this enquiry"
                order={5}
                fw={600}
                fz="h1"
              />
            </Group>
            <Container>
              <Group align="center">
                <IconNotes color="#ee347d" />
                <TextInput
                  placeholder="Add Note"
                  value={noteText}
                  onChange={(e) => setNoteText(e.currentTarget.value)}
                  style={{ flex: 1 }}
                />
                <Button
                  leftIcon={<IconCirclePlus size={16} />}
                  onClick={handleAddNoteUI}
                  disabled={!noteText.trim() || !canUpdate}
                >
                  Add
                </Button>
              </Group>
            </Container>
            {notes.length > 0 && (
              <div style={{ marginTop: "1rem" }}>
                {notes.map((note: any, index: number) => (
                  <Paper
                    key={note.id || index}
                    withBorder
                    p="sm"
                    mt="xs"
                    radius="sm"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div>
                      <Text
                        size="sm"
                        fw={500}
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {note.text}
                      </Text>
                      <Text size="xs" color="dimmed">
                        {note.created_at instanceof Date
                          ? note.created_at.toLocaleString()
                          : note.created_at instanceof Timestamp
                            ? note.created_at.toDate().toLocaleString()
                            : ""}
                        {note.created_by_name
                          ? ` by ${note.created_by_name}`
                          : ""}
                      </Text>
                    </div>
                    {canUpdate && (
                      <Stack>
                        <ActionIcon
                          color="red"
                          variant="light"
                          onClick={() => handleEditNote(note)}
                        >
                          <IconPencil size={16} />
                        </ActionIcon>
                        <ActionIcon
                          color="red"
                          variant="light"
                          onClick={() => handleDeleteNote(note.id)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Stack>
                    )}
                  </Paper>
                ))}
              </div>
            )}
          </BenchCard>
        </BenchCard>
      </Container>
      <Modal
        opened={campaignModalOpen}
        onClose={() => setCampaignModalOpen(false)}
        title={<Title order={4}>Add New Campaign</Title>}
        closeOnClickOutside={false}
      >
        <TextInput
          label="Enter Campaign Name"
          value={newCampaignName}
          onChange={(e) => setNewCampaignName(e.target.value)}
          mb="lg"
          required
        />
        <Button mt="md" onClick={handleNewCampaign}>
          Submit
        </Button>
      </Modal>

      <Modal
        opened={sourceModalOpen}
        onClose={() => setSourceModalOpen(false)}
        title={<Title order={4}>Add New Source</Title>}
        closeOnClickOutside={false}
      >
        <TextInput
          label="Enter Source Name"
          value={newSourceName}
          onChange={(e) => setNewSourceName(e.target.value)}
          mb="lg"
          required
        />
        <Button mt="md" onClick={handleNewSource}>
          Submit
        </Button>
      </Modal>

      <Modal
        opened={editNoteModalOpen}
        onClose={() => setEditNoteModalOpen(false)}
        title={<Title order={4}>Edit Note</Title>}
        closeOnClickOutside={false}
      >
        <Textarea
          label="Note Text"
          value={editingNote?.text || ""}
          onChange={(e) => {
            const val = e.currentTarget.value;
            setEditingNote((prev) => (prev ? { ...prev, text: val } : null));
          }}
          mb="lg"
          rows={4}
        />
        <Group position="right">
          <Button variant="outline" onClick={() => setEditNoteModalOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleUpdateNote}>Update Note</Button>
        </Group>
      </Modal>

      <Modal
        opened={conversionModalOpen}
        onClose={() => setConversionModalOpen(false)}
        title={<Title order={4}>Lead Not Enrolled as Member</Title>}
        closeOnClickOutside={false}
      >
        <Text size="sm" mb="lg">
          This lead is not yet registered as a member. You must convert this
          enquiry to a member before setting the status to 'Enrolled'.
        </Text>
        <Group position="right">
          {/* <Button variant="outline" onClick={() => setConversionModalOpen(false)}>Cancel</Button> */}
          {/* <Button
                        color="blue"
                        onClick={() => {
                            setConversionModalOpen(false);
                            navigate(ROUTES.ADD_MEMBER_PAGE, {
                                state: {
                                    enquiryData: {
                                        ...enquiryData,
                                        enquiry_id: enquiryId,
                                        branch_id: FilterId,
                                        company_id: companyId
                                    }
                                }
                            });
                        }}
                    >
                        Convert to Member
                    </Button> */}
        </Group>
      </Modal>

      <BenchLoader isLoading={isPageLoading} />
    </div>
  );
}
